import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Trophy } from 'lucide-react';
import { StandingsTable } from './StandingsTable';
import { standingsLayout } from '../../utils/category-standings';
import { properName } from '../../utils/proper-name';
import type { CategoryStandings } from '../../services/category-standings';

interface CategoryStandingsSheetProps {
  standings: CategoryStandings;
  /**
   * Drawn as a stage's results, which are the class's places and nothing
   * else: the place first, and no word of the rounds — no heading naming them
   * (still drawn when the class has more than one table, or the tables could
   * not be told apart), no turn column, no judged blocks — and the names in
   * one case (`properName`), however the desk typed them.
   */
  results?: boolean;
}

/**
 * One class's standings across its rounds, as the room reads them: its turns
 * ranked as **one** table with the turn each athlete climbed in, then each of
 * its other rounds that is closed (a final) on its own. Turns that cannot be
 * merged (a different number of blocks) are one table each, and it says why.
 *
 * Like `RoundStandings`: three columns (four with the turn), and only the people
 * who are placed — somebody with nothing on any card is not last, they did not
 * climb. The places are the server's, by the competition's regulation; under
 * one that tells judged blocks apart (OPES) each table names its judged blocks.
 */
export function CategoryStandingsSheet({ standings, results = false }: CategoryStandingsSheetProps) {
  const { t } = useTranslation();

  const layout = useMemo(() => standingsLayout(standings), [standings]);

  const sections = layout.sections.map((section) => {
    const roundNames = new Map(section.rounds.map((round) => [round.id, round.name]));
    return {
      section,
      rows: section.rows
        .filter((row) => row.rank != null)
        .map(({ entry, rank }) => ({
          id: entry.id,
          name: results && entry.full_name ? properName(entry.full_name) : entry.full_name,
          team: entry.team_name,
          hasAccount: entry.has_account,
          avatarUrl: entry.avatar_url,
          profileSlug: entry.user_slug,
          turn: entry.round_id ? roundNames.get(entry.round_id) ?? null : null,
          position: rank as number,
          // A stage's results are the places and nothing else.
          tiebreak: results ? null : entry.tiebreak,
        })),
    };
  });

  const headed = !results || sections.length > 1;

  if (sections.every(({ rows }) => rows.length === 0)) {
    return (
      <div className="py-8 text-center text-muted-foreground" data-testid="category-standings-empty">
        <Trophy className="mx-auto mb-4 size-12 opacity-50" />
        <p>{t('competitionStandings.empty')}</p>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="category-standings">
      {layout.incomparable && (
        <p className="text-sm text-muted-foreground" data-testid="category-standings-incomparable">
          {t(`competitionStandings.incomparable.${layout.incomparable}`, {
            blocks: layout.judgedMismatch.join(', '),
          })}
        </p>
      )}

      {sections.map(({ section, rows }) => (
        <section key={section.key} className="space-y-2" data-testid={`category-standings-${section.key}`}>
          {headed && <h3 className="font-semibold">{section.rounds.map((round) => round.name).join(' + ')}</h3>}

          {!results && layout.judgedBlocks && section.roundType === 'Boulder' && section.judgedNumbers.length > 0 && (
            <p className="text-xs text-muted-foreground" data-testid="category-standings-judged-blocks">
              {t('competitionRankings.judgedBlocks', { blocks: section.judgedNumbers.join(', ') })}
            </p>
          )}

          {section.mixedStyles && rows.length > 0 && (
            <p className="text-xs text-muted-foreground">{t('leadRounds.style.mixedNote')}</p>
          )}

          {rows.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t('competitionStandings.empty')}</p>
          ) : (
            <StandingsTable rows={rows} showTurn={!results && section.showTurn} positionFirst={results} />
          )}
        </section>
      ))}
    </div>
  );
}

export default CategoryStandingsSheet;
